// ==========================================
// Shared API error normalization
// ==========================================
//
// QG-CLI-COMP-002: the generated SDK's `unwrapGenerated()` used to throw a hardcoded
// 'API request failed' for any non-string, non-Error error value — which is every
// server error, since the generated client always hands it the parsed JSON body. The
// hand-rolled `request()` in `client.ts` had its own, narrower `error.error ||
// error.message` fallback that rendered `[object Object]` for the nested
// `{ error: { code, message } }` shape. Both stacks now share this single normalizer
// covering all four server error-body shapes (QG-SRV-SVC-013):
//   { code, message }
//   { error, details }
//   { error, code }
//   { error: { code, message } }

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  /** The request field a validation error is about (`ServiceError.field`), when the server names one. */
  field?: string;

  constructor(message: string, status: number, options?: { code?: string; details?: unknown; field?: string }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = options?.code;
    this.details = options?.details;
    this.field = options?.field;
  }
}

/**
 * Normalizes a parsed server error body (or a plain string) into an `ApiError`.
 * Priority: `body.error?.message` → `body.message` → `body.error` (when it is a
 * string) → `HTTP ${status}`. `code`/`details` are attached from whichever shape
 * carried them, and `status` (the HTTP status of the response, not part of the
 * body) is always attached so callers can branch on it (e.g. 409 = already-done).
 */
export function normalizeApiError(body: unknown, status: number): ApiError {
  if (typeof body === 'string' && body.length > 0) {
    return new ApiError(body, status);
  }

  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const nestedError = record.error;
  const nestedRecord =
    nestedError && typeof nestedError === 'object' ? (nestedError as Record<string, unknown>) : undefined;

  const message =
    (nestedRecord && typeof nestedRecord.message === 'string' ? nestedRecord.message : undefined) ??
    (typeof record.message === 'string' ? record.message : undefined) ??
    (typeof nestedError === 'string' && nestedError.length > 0 ? nestedError : undefined) ??
    `HTTP ${status}`;

  const code =
    (nestedRecord && typeof nestedRecord.code === 'string' ? nestedRecord.code : undefined) ??
    (typeof record.code === 'string' ? record.code : undefined);

  const details = (nestedRecord ? nestedRecord.details : undefined) ?? record.details;

  const field =
    (nestedRecord && typeof nestedRecord.field === 'string' ? nestedRecord.field : undefined) ??
    (typeof record.field === 'string' ? record.field : undefined);

  return new ApiError(message, status, { code, details, field });
}
